import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  ArrowRightLeft,
  CircleAlert,
  GripVertical,
  LoaderCircle,
  MapPinned,
  Plus,
  Save,
  X,
} from 'lucide-react';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useAuth } from '../auth/useAuth';
import { requireFirestore } from '../lib/firebase';
import {
  createProvinceQueueId,
  defaultProvinceQueueConfiguration,
  provincesInQueue,
  QUEUE_PROVINCES,
  queueNameExists,
  storedProvinceQueueConfiguration,
  unassignedQueueProvinces,
  type Province,
  type ProvinceQueue,
  type ProvinceQueueConfiguration,
} from '../lib/province-queues';

type SaveStatus = 'loading' | 'ready' | 'saving' | 'saved' | 'error';

const settingsDocument = 'provinceQueues';
const demoStorageKey = 'report-crm:province-queues';
const unassignedDropId = 'unassigned-provinces';
const provinceDragPrefix = 'province:';
const queueDropPrefix = 'queue:';

function readDemoConfiguration(): unknown {
  try {
    const stored = localStorage.getItem(demoStorageKey);
    return stored ? JSON.parse(stored) : undefined;
  } catch {
    return undefined;
  }
}

function provinceDragId(provinceId: string): string {
  return `${provinceDragPrefix}${provinceId}`;
}

function queueDropId(queueId: string): string {
  return `${queueDropPrefix}${queueId}`;
}

function provinceIdFromDragId(value: string): string | null {
  if (!value.startsWith(provinceDragPrefix)) return null;
  const provinceId = value.slice(provinceDragPrefix.length);
  return QUEUE_PROVINCES.some(({ id }) => id === provinceId) ? provinceId : null;
}

function queueIdFromDropId(value: string, queues: readonly ProvinceQueue[]): string | null {
  if (!value.startsWith(queueDropPrefix)) return null;
  const queueId = value.slice(queueDropPrefix.length);
  return queues.some(({ id }) => id === queueId) ? queueId : null;
}

function ProvinceRow({ province, disabled }: { province: Province; disabled: boolean }) {
  const { attributes, isDragging, listeners, setNodeRef } = useDraggable({
    id: provinceDragId(province.id),
    disabled,
  });

  return (
    <li
      ref={setNodeRef}
      className={isDragging ? 'dragging' : undefined}
      title={`Trascina ${province.name}`}
      aria-label={`Sposta ${province.name}`}
      {...attributes}
      {...listeners}
      aria-describedby="province-queue-drag-help"
    >
      <GripVertical className="territorial-drag-handle" size={16} aria-hidden="true" />
      <strong>{province.name}</strong>
    </li>
  );
}

function QueueCard({
  disabled,
  index,
  provinces,
  queue,
}: {
  disabled: boolean;
  index: number;
  provinces: readonly Province[];
  queue: ProvinceQueue;
}) {
  const { isOver, setNodeRef } = useDroppable({
    id: queueDropId(queue.id),
    disabled,
  });

  return (
    <section
      ref={setNodeRef}
      className={`province-queue-card queue-tone-${(index % 6) + 1}${isOver ? ' drop-target' : ''}`}
      aria-labelledby={`queue-${queue.id}`}
    >
      <header>
        <span>{String(index + 1).padStart(2, '0')}</span>
        <div>
          <h3 id={`queue-${queue.id}`}>{queue.name}</h3>
          <p>{provinces.length} {provinces.length === 1 ? 'provincia' : 'province'}</p>
        </div>
      </header>

      <ul className="province-queue-list">
        {provinces.length === 0 && <li className="province-queue-empty">Rilascia qui una provincia</li>}
        {provinces.map((province) => (
          <ProvinceRow key={province.id} province={province} disabled={disabled} />
        ))}
      </ul>
    </section>
  );
}

function UnassignedProvinces({
  disabled,
  provinces,
}: {
  disabled: boolean;
  provinces: readonly Province[];
}) {
  const { isOver, setNodeRef } = useDroppable({ id: unassignedDropId, disabled });

  return (
    <section
      ref={setNodeRef}
      className={`territorial-unassigned province-queue-unassigned${isOver ? ' drop-target' : ''}`}
      aria-labelledby="unassigned-provinces-title"
    >
      <header>
        <span><CircleAlert size={19} /></span>
        <div>
          <h3 id="unassigned-provinces-title">Non allocate</h3>
          <p>{provinces.length > 0
            ? `${provinces.length} ${provinces.length === 1 ? 'provincia da assegnare' : 'province da assegnare'}`
            : 'Trascina qui una provincia per rimuoverla dalla sua coda.'}</p>
        </div>
      </header>
      <ul className="territorial-unassigned-regions province-queue-unassigned-list">
        {provinces.length === 0 && <li className="territorial-unassigned-empty">Nessuna provincia non allocata</li>}
        {provinces.map((province) => (
          <ProvinceRow key={province.id} province={province} disabled={disabled} />
        ))}
      </ul>
    </section>
  );
}

export function ProvinceQueuesPage() {
  const { user } = useAuth();
  const [configuration, setConfiguration] = useState<ProvinceQueueConfiguration>(
    defaultProvinceQueueConfiguration,
  );
  const [status, setStatus] = useState<SaveStatus>('loading');
  const [dirty, setDirty] = useState(false);
  const [activeProvinceId, setActiveProvinceId] = useState<string | null>(null);
  const [showQueueForm, setShowQueueForm] = useState(false);
  const [queueName, setQueueName] = useState('');
  const [queueNameError, setQueueNameError] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  useEffect(() => {
    let active = true;
    const load = user?.demo
      ? Promise.resolve(readDemoConfiguration())
      : getDoc(doc(requireFirestore(), 'settings', settingsDocument))
          .then((snapshot) => snapshot.data());

    void load
      .then((stored) => {
        if (!active) return;
        setConfiguration(storedProvinceQueueConfiguration(stored));
        setStatus('ready');
      })
      .catch(() => {
        if (active) setStatus('error');
      });

    return () => {
      active = false;
    };
  }, [user?.demo]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [dirty]);

  const unassigned = useMemo(
    () => unassignedQueueProvinces(configuration.assignments),
    [configuration.assignments],
  );
  const assignedCount = QUEUE_PROVINCES.length - unassigned.length;
  const activeProvince = activeProvinceId
    ? QUEUE_PROVINCES.find(({ id }) => id === activeProvinceId) ?? null
    : null;

  const updateAssignment = (provinceId: string, queueId: string | null) => {
    setConfiguration((current) => ({
      ...current,
      assignments: { ...current.assignments, [provinceId]: queueId },
    }));
    setDirty(true);
    setStatus('ready');
  };

  const startDragging = ({ active }: DragStartEvent) => {
    setActiveProvinceId(provinceIdFromDragId(String(active.id)));
  };

  const finishDragging = ({ active, over }: DragEndEvent) => {
    const provinceId = provinceIdFromDragId(String(active.id));
    const destination = over ? String(over.id) : '';
    const nextQueueId = destination === unassignedDropId
      ? null
      : queueIdFromDropId(destination, configuration.queues);

    if (provinceId && (destination === unassignedDropId || nextQueueId)) {
      if (configuration.assignments[provinceId] !== nextQueueId) {
        updateAssignment(provinceId, nextQueueId);
      }
    }
    setActiveProvinceId(null);
  };

  const closeQueueForm = () => {
    setShowQueueForm(false);
    setQueueName('');
    setQueueNameError(null);
  };

  const addQueue = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = queueName.trim().replace(/\s+/g, ' ');
    if (!name) {
      setQueueNameError('Inserisci il nome della nuova coda.');
      return;
    }
    if (queueNameExists(name, configuration.queues)) {
      setQueueNameError('Esiste già una coda con questo nome.');
      return;
    }

    setConfiguration((current) => ({
      ...current,
      queues: [
        ...current.queues,
        { id: createProvinceQueueId(name, current.queues), name },
      ],
    }));
    setDirty(true);
    setStatus('ready');
    closeQueueForm();
  };

  const save = async () => {
    setStatus('saving');
    try {
      if (user?.demo) {
        localStorage.setItem(demoStorageKey, JSON.stringify(configuration));
      } else {
        await setDoc(doc(requireFirestore(), 'settings', settingsDocument), {
          version: 1,
          queues: configuration.queues,
          assignments: configuration.assignments,
          updatedAt: serverTimestamp(),
          updatedBy: user?.email ?? null,
        }, { merge: true });
      }
      setDirty(false);
      setStatus('saved');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="settings-tab-panel province-queues-page" aria-labelledby="province-queues-title">
      <header className="page-heading province-queues-heading">
        <div>
          <p className="app-eyebrow">Organizzazione commerciale</p>
          <h2 id="province-queues-title">Gestione code e province</h2>
          <p>Assegna ogni provincia alla coda di competenza e crea nuove code quando necessario.</p>
        </div>
        <button
          className="button button-secondary"
          type="button"
          disabled={status === 'loading' || status === 'saving'}
          onClick={() => setShowQueueForm(true)}
        >
          <Plus size={17} /> Nuova coda
        </button>
      </header>

      <section className="territorial-areas-summary province-queues-summary" aria-label="Riepilogo configurazione">
        <span className="territorial-areas-summary-icon"><MapPinned size={22} /></span>
        <div>
          <strong>{configuration.queues.length} code disponibili</strong>
          <p>Ogni provincia può appartenere a una sola coda.</p>
        </div>
        <dl>
          <div><dt>Assegnate</dt><dd>{assignedCount}</dd></div>
          <div className={unassigned.length > 0 ? 'attention' : undefined}><dt>Non allocate</dt><dd>{unassigned.length}</dd></div>
        </dl>
      </section>

      {showQueueForm && (
        <form className="province-queue-add-form" onSubmit={addQueue}>
          <label htmlFor="new-queue-name">
            <span>Nome della nuova coda</span>
            <input
              id="new-queue-name"
              type="text"
              value={queueName}
              maxLength={100}
              autoFocus
              aria-invalid={Boolean(queueNameError)}
              aria-describedby={queueNameError ? 'new-queue-error' : undefined}
              placeholder="Es. Lombardia Centro"
              onChange={(event) => {
                setQueueName(event.target.value);
                setQueueNameError(null);
              }}
            />
          </label>
          <div>
            <button className="button button-primary" type="submit"><Plus size={16} /> Aggiungi coda</button>
            <button className="button button-secondary" type="button" onClick={closeQueueForm}>
              <X size={16} /> Annulla
            </button>
          </div>
          {queueNameError && <p id="new-queue-error" role="alert">{queueNameError}</p>}
        </form>
      )}

      <div className="territorial-drag-help" id="province-queue-drag-help">
        <GripVertical size={17} />
        <span><strong>Trascina e rilascia</strong> una provincia sulla coda di destinazione o in Non allocate.</span>
      </div>

      {status === 'error' && (
        <div className="territorial-feedback error" role="alert">
          Non è stato possibile leggere o salvare la configurazione. Riprova tra qualche istante.
        </div>
      )}

      {status === 'loading' ? (
        <div className="territorial-areas-loading" role="status">
          <LoaderCircle className="spin" size={22} /> Caricamento delle code…
        </div>
      ) : (
        <>
          <DndContext
            sensors={sensors}
            onDragStart={startDragging}
            onDragCancel={() => setActiveProvinceId(null)}
            onDragEnd={finishDragging}
          >
            <div className="province-queues-grid">
              {configuration.queues.map((queue, index) => (
                <QueueCard
                  key={queue.id}
                  disabled={status === 'saving'}
                  index={index}
                  provinces={provincesInQueue(configuration.assignments, queue.id)}
                  queue={queue}
                />
              ))}
            </div>

            <UnassignedProvinces provinces={unassigned} disabled={status === 'saving'} />

            <DragOverlay>
              {activeProvince && (
                <div className="territorial-drag-overlay province-queue-drag-overlay">
                  <GripVertical size={16} />
                  <strong>{activeProvince.name}</strong>
                </div>
              )}
            </DragOverlay>
          </DndContext>

          <footer className="territorial-areas-footer">
            <p>
              <ArrowRightLeft size={16} />
              {dirty
                ? 'Ci sono modifiche non salvate.'
                : status === 'saved'
                  ? 'Configurazione salvata correttamente.'
                  : 'Trascina le province tra le code, poi salva.'}
            </p>
            <button className="button button-primary" type="button" disabled={!dirty || status === 'saving'} onClick={() => void save()}>
              {status === 'saving' ? <LoaderCircle className="spin" size={18} /> : <Save size={18} />}
              {status === 'saving' ? 'Salvataggio…' : 'Salva configurazione'}
            </button>
          </footer>
        </>
      )}
    </section>
  );
}
